import { z } from "zod";
import { IsoDate, Uuid } from "./common.js";

// ---- Delivery rules (ADR 0027, migration 0028) ------------------------------------------
//
// A study promises its results one of three ways:
//   - nothing (`delivery_days` and `delivery_by_weekday` both null): no promised date;
//   - a fixed turnaround, `delivery_days` = N (0 = the same day);
//   - a weekday table, `delivery_by_weekday` = 7 day offsets indexed by the weekday the sample
//     is received, Monday first. The pilot's culture note ("Lunes → Jueves … Domingo → Jueves")
//     is [3, 3, 3, 4, 4, 3, 4].
// The two columns are exclusive. Offsets, not target weekdays, are stored: "Lunes → Lunes" is
// ambiguous (same day or next week) and an offset is not.

/** Largest offset a weekday table may promise (two weeks). */
export const MAX_DELIVERY_OFFSET = 14;

export const DeliveryByWeekday = z
  .array(z.number().int().min(0).max(MAX_DELIVERY_OFFSET))
  .length(7, "La tabla de entrega lleva un valor por cada día de la semana.");
export type DeliveryByWeekday = z.infer<typeof DeliveryByWeekday>;

export interface DeliveryRule {
  delivery_days: number | null;
  delivery_by_weekday?: readonly number[] | null;
}

/** Monday-first weekday names, es-MX. */
export const WEEKDAYS_ES = ["lunes", "martes", "miércoles", "jueves", "viernes", "sábado", "domingo"] as const;
export const WEEKDAYS_SHORT_ES = ["lun", "mar", "mié", "jue", "vie", "sáb", "dom"] as const;

/** 0 = Monday … 6 = Sunday, for a YYYY-MM-DD calendar day (no time zone involved). */
export function weekdayIndex(isoDay: string): number {
  const d = new Date(`${isoDay}T00:00:00Z`).getUTCDay(); // 0 = Sunday
  return (d + 6) % 7;
}

/** A calendar day plus `n` days (negative allowed), YYYY-MM-DD. */
export function addIsoDays(isoDay: string, n: number): string {
  const d = new Date(`${isoDay}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

/** Whole days from `a` to `b` (b − a). */
export function isoDayDiff(a: string, b: string): number {
  return Math.round((Date.parse(`${b}T00:00:00Z`) - Date.parse(`${a}T00:00:00Z`)) / 86_400_000);
}

/** The offset (days) the rule promises for a sample received on `receiptDay`; null = no promise. */
export function deliveryOffset(receiptDay: string, rule: DeliveryRule): number | null {
  const table = rule.delivery_by_weekday;
  if (table && table.length === 7) return table[weekdayIndex(receiptDay)] ?? null;
  return rule.delivery_days ?? null;
}

/** The promised delivery day for a sample received on `receiptDay` (lab-local), or null. */
export function dueDateFor(receiptDay: string, rule: DeliveryRule): string | null {
  const off = deliveryOffset(receiptDay, rule);
  return off === null ? null : addIsoDays(receiptDay, off);
}

/** The latest of several rules' promises for the same receipt day; null when none promises. */
export function latestDueDate(receiptDay: string, rules: readonly DeliveryRule[]): string | null {
  let best: string | null = null;
  for (const r of rules) {
    const d = dueDateFor(receiptDay, r);
    if (d !== null && (best === null || d > best)) best = d;
  }
  return best;
}

/** What one weekday-table option reads like: "El mismo día", "Jueves", "Lunes siguiente", "Martes de la otra semana". */
export function offsetLabel(receiptWeekday: number, offset: number): string {
  if (offset === 0) return "El mismo día";
  const target = WEEKDAYS_ES[(receiptWeekday + offset) % 7]!;
  const cap = target.charAt(0).toUpperCase() + target.slice(1);
  if (offset < 7) return cap;
  if (offset === 7) return `${cap} siguiente`;
  return `${cap} de la otra semana`;
}

/** One-line summary of a rule for lists: "Mismo día", "3 días", "Según el día: lun→jue, mar→vie…". */
export function deliveryRuleSummary(rule: DeliveryRule): string {
  const table = rule.delivery_by_weekday;
  if (table && table.length === 7) {
    return `Según el día: ${table.map((off, i) => `${WEEKDAYS_SHORT_ES[i]}→${off === 0 ? "mismo día" : WEEKDAYS_SHORT_ES[(i + off) % 7]}`).join(", ")}`;
  }
  if (rule.delivery_days === null || rule.delivery_days === undefined) return "—";
  if (rule.delivery_days === 0) return "Mismo día";
  if (rule.delivery_days === 1) return "1 día";
  return `${rule.delivery_days} días`;
}

// ---- Calendar (Calendario) --------------------------------------------------------------

/** Where a study due that day stands. */
export const DueStatus = z.enum(["pending", "ready", "delivered"]);
export type DueStatus = z.infer<typeof DueStatus>;

/** One study whose promised delivery falls on `due_date`. */
export const CalendarDueItem = z.object({
  order_item_id: Uuid,
  order_id: Uuid,
  folio: z.string(),
  branch_id: Uuid,
  patient_name: z.string(),
  study_clave: z.string(),
  study_name: z.string(),
  due_date: IsoDate,
  /** pending = not validated yet; ready = validated, not delivered; delivered = a report with it was delivered. */
  status: DueStatus,
  /** The item's state, for the finer chip (captured = "por validar"). */
  item_state: z.enum(["pending", "captured", "validated", "issued", "amended", "cancelled"]),
  /** A tube of this study is still to be collected: the date is provisional. */
  sample_pending: z.boolean(),
  /** Promised for the registration day itself (a same-day study): the calendar hides these unless asked. */
  same_day: z.boolean(),
  /** Still pending after its day (lab-local today). */
  overdue: z.boolean(),
});
export type CalendarDueItem = z.infer<typeof CalendarDueItem>;

/**
 * How a note repeats (migration 0031, Fer 2026-11-03: "cada dos meses", the electricity bill): never,
 * every N weeks, or every N months (N = `recurrence_interval`, 1–12), until an optional end day.
 */
export const CalendarRecurrence = z.enum(["none", "weekly", "monthly"]);
export type CalendarRecurrence = z.infer<typeof CalendarRecurrence>;
export const MAX_RECURRENCE_INTERVAL = 12;
const RecurrenceInterval = z.number().int().min(1).max(MAX_RECURRENCE_INTERVAL);

const Time = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, "Hora HH:MM.");

/** A note the laboratory's staff put on the calendar (deposit, RPBI pickup, an appointment…). */
export const CalendarEntry = z.object({
  id: Uuid,
  title: z.string(),
  /** First (or only) day. A monthly entry repeats on this day of the month (clamped to a shorter month's last day). */
  entry_date: IsoDate,
  entry_time: Time.nullable(),
  /** null = every branch. */
  branch_id: Uuid.nullable(),
  note: z.string().nullable(),
  recurrence: CalendarRecurrence,
  /** Every N weeks or months; 1 for a one-off note. */
  recurrence_interval: RecurrenceInterval,
  /** Last day an occurrence may fall on (inclusive); null = no end. */
  recurrence_until: IsoDate.nullable(),
  patient_id: Uuid.nullable(),
  /** Display name of the linked patient, from the patient record. */
  patient_name: z.string().nullable(),
  order_id: Uuid.nullable(),
  order_folio: z.string().nullable(),
  /** Occurrences marked done (YYYY-MM-DD). */
  done_dates: z.array(IsoDate),
  created_by_name: z.string().nullable(),
});
export type CalendarEntry = z.infer<typeof CalendarEntry>;

/** One day on which an entry falls. */
export const CalendarOccurrence = z.object({
  date: IsoDate,
  done: z.boolean(),
  entry: CalendarEntry,
});
export type CalendarOccurrence = z.infer<typeof CalendarOccurrence>;

/** GET /v1/calendar — inclusive lab-local day range, at most 62 days. */
export const CalendarQuery = z
  .object({
    from: IsoDate,
    to: IsoDate,
    /** Absent = every branch. Entries for every branch always show. */
    branch_id: Uuid.optional(),
  })
  .superRefine((q, ctx) => {
    if (q.to < q.from) ctx.addIssue({ code: "custom", message: "El rango termina antes de empezar.", path: ["to"] });
    else if (isoDayDiff(q.from, q.to) > 62) ctx.addIssue({ code: "custom", message: "El rango es de 62 días como máximo.", path: ["to"] });
  });
export type CalendarQuery = z.infer<typeof CalendarQuery>;

export const CalendarView = z.object({
  /** Lab-local today, so the client highlights the lab's day, not the device's. */
  today: IsoDate,
  due: z.array(CalendarDueItem),
  /** Still pending with a due day before today, whatever the range (oldest first, at most 200). */
  overdue: z.array(CalendarDueItem),
  entries: z.array(CalendarOccurrence),
});
export type CalendarView = z.infer<typeof CalendarView>;

export const CalendarEntryUpsertPayload = z
  .object({
  /** null = a new entry (its id is the command id). */
  entry_id: Uuid.nullable().default(null),
  title: z.string().trim().min(1, "Escribe qué hay que hacer.").max(120),
  entry_date: IsoDate,
  entry_time: Time.nullable().default(null),
  branch_id: Uuid.nullable().default(null),
  note: z.string().trim().max(1000).nullable().default(null),
  recurrence: CalendarRecurrence.default("none"),
  recurrence_interval: RecurrenceInterval.default(1),
  recurrence_until: IsoDate.nullable().default(null),
  patient_id: Uuid.nullable().default(null),
  order_id: Uuid.nullable().default(null),
  })
  .superRefine((p, ctx) => {
    if (p.recurrence !== "none" && p.recurrence_until !== null && p.recurrence_until < p.entry_date) {
      ctx.addIssue({ code: "custom", message: "La fecha final es antes de la primera.", path: ["recurrence_until"] });
    }
  });
export type CalendarEntryUpsertPayload = z.infer<typeof CalendarEntryUpsertPayload>;

export const CalendarEntryRemovePayload = z.object({ entry_id: Uuid });
export type CalendarEntryRemovePayload = z.infer<typeof CalendarEntryRemovePayload>;

/** Tick (or untick) one occurrence of an entry. */
export const CalendarEntryDonePayload = z.object({ entry_id: Uuid, date: IsoDate, done: z.boolean() });
export type CalendarEntryDonePayload = z.infer<typeof CalendarEntryDonePayload>;

type Recurring = Pick<CalendarEntry, "entry_date" | "recurrence"> & Partial<Pick<CalendarEntry, "recurrence_interval" | "recurrence_until">>;

/** The `k`-th monthly occurrence: `k * interval` months after the first, the day clamped to the month's end. */
function monthlyDate(start: string, months: number): string {
  const y0 = Number(start.slice(0, 4));
  const m0 = Number(start.slice(5, 7)) - 1 + months;
  const y = y0 + Math.floor(m0 / 12);
  const m = ((m0 % 12) + 12) % 12;
  const last = new Date(Date.UTC(y, m + 1, 0)).getUTCDate();
  const day = Math.min(Number(start.slice(8, 10)), last);
  return `${y}-${String(m + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

/**
 * The days in [from, to] on which an entry falls. Weekly: the first day plus every `7 × N` days.
 * Monthly: the first day's day of month every N months, a 31st falling on the last day of a shorter
 * month (and back on the 31st afterwards: each occurrence counts from the first, never from the last).
 * Nothing before the first day or after `recurrence_until`.
 */
export function entryOccurrences(entry: Recurring, from: string, to: string): string[] {
  const start = entry.entry_date;
  const until = entry.recurrence_until ?? null;
  const last = until !== null && until < to ? until : to;
  const first = start > from ? start : from;
  if (first > last) return [];
  if (entry.recurrence === "none") return start >= from && start <= last ? [start] : [];
  const n = Math.max(1, entry.recurrence_interval ?? 1);
  const out: string[] = [];
  if (entry.recurrence === "weekly") {
    const step = 7 * n;
    let k = Math.max(0, Math.ceil(isoDayDiff(start, first) / step));
    for (let d = addIsoDays(start, k * step); d <= last; k++, d = addIsoDays(start, k * step)) out.push(d);
    return out;
  }
  // Monthly: start from the occurrence just before the window, then walk forward.
  const monthsToFirst = (Number(first.slice(0, 4)) - Number(start.slice(0, 4))) * 12 + Number(first.slice(5, 7)) - Number(start.slice(5, 7));
  for (let k = Math.max(0, Math.floor(monthsToFirst / n) - 1); ; k++) {
    const d = monthlyDate(start, k * n);
    if (d > last) break;
    if (d >= first) out.push(d);
  }
  return out;
}

/** How a note repeats, for a list: "Cada 2 meses hasta el 3 de marzo de 2027"; null when it does not. */
export function recurrenceText(entry: Pick<CalendarEntry, "recurrence"> & Partial<Pick<CalendarEntry, "recurrence_interval" | "recurrence_until">>): string | null {
  if (entry.recurrence === "none") return null;
  const n = entry.recurrence_interval ?? 1;
  const unit = entry.recurrence === "weekly" ? (n === 1 ? "semana" : "semanas") : n === 1 ? "mes" : "meses";
  const base = n === 1 ? `Cada ${unit}` : `Cada ${n} ${unit}`;
  if (!entry.recurrence_until) return base;
  const until = new Intl.DateTimeFormat("es-MX", { timeZone: "UTC", day: "numeric", month: "long", year: "numeric" }).format(new Date(`${entry.recurrence_until}T12:00:00Z`));
  return `${base} hasta el ${until}`;
}
